import { useState, useEffect } from 'react'
import { Plus, Search, Edit2, Trash2, Shield, CheckSquare, Square, AlertCircle } from 'lucide-react'
import { apiClient } from '../services/api'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Role, Permission, PermissionsByModule } from '../types/api'

interface RoleFormData {
  name: string
  description: string
  is_active: boolean
  permission_ids: string[]
}

export const RoleManagement = () => {
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<PermissionsByModule>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [viewMode, setViewMode] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [initializing, setInitializing] = useState(false)

  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    is_active: true,
    permission_ids: []
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [rolesResponse, permissionsResponse] = await Promise.all([
        apiClient.getRoles(),
        apiClient.getPermissions()
      ])
      setRoles(rolesResponse.roles || [])
      setPermissions(permissionsResponse.permissions || {})
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const submitData = {
        ...formData,
        is_active: formData.is_active ? 1 : 0
      }

      if (editingRole) {
        await apiClient.updateRole(editingRole.id, submitData)
      } else {
        await apiClient.createRole(submitData)
      }
      await loadData()
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save role')
    }
  }

  const handleEdit = (role: Role) => {
    setEditingRole(role)
    setViewMode(false)
    setFormData({
      name: role.name,
      description: role.description || '',
      is_active: role.is_active === 1,
      permission_ids: role.permissions?.map(p => p.id) || []
    })
    setShowForm(true)
  }

  const handleView = (role: Role) => {
    setEditingRole(role)
    setViewMode(true)
    setFormData({
      name: role.name,
      description: role.description || '',
      is_active: role.is_active === 1,
      permission_ids: role.permissions?.map(p => p.id) || []
    })
    setShowForm(true)
  }

  const handleDelete = async (roleId: string, roleName: string) => {
    if (!confirm(`Are you sure you want to delete the role "${roleName}"? This action cannot be undone.`)) return

    try {
      await apiClient.deleteRole(roleId)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role')
    }
  }

  const handleInitializeRoles = async () => {
    if (!confirm('This will create default roles (Admin, Manager, Cashier, Viewer). Continue?')) return

    try {
      setInitializing(true)
      await apiClient.initializeDefaultRoles()
      await loadData()
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize roles')
    } finally {
      setInitializing(false)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      is_active: true,
      permission_ids: []
    })
    setEditingRole(null)
    setViewMode(false)
    setShowForm(false)
  }

  const togglePermission = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId]
    }))
  }

  const toggleModulePermissions = (modulePermissions: Permission[]) => {
    const modulePermissionIds = modulePermissions.map(p => p.id)
    const allSelected = modulePermissionIds.every(id => formData.permission_ids.includes(id))

    setFormData(prev => ({
      ...prev,
      permission_ids: allSelected
        ? prev.permission_ids.filter(id => !modulePermissionIds.includes(id))
        : [...new Set([...prev.permission_ids, ...modulePermissionIds])]
    }))
  }

  const filteredRoles = (roles || []).filter(role =>
    role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Role Management</h1>
          <p className="text-gray-600">Manage user roles and permissions</p>
        </div>
        <div className="flex gap-2">
          {roles.length === 0 && (
            <Button
              onClick={handleInitializeRoles}
              disabled={initializing}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Shield className="h-4 w-4" />
              {initializing ? 'Initializing...' : 'Initialize Default Roles'}
            </Button>
          )}
          <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Role
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Search */}
      <Card className="p-6">
        <div className="max-w-md relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">Search Roles</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or description..."
              className="pl-10"
            />
          </div>
        </div>
      </Card>

      {/* Role Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {viewMode ? 'View Role' : editingRole ? 'Edit Role' : 'Add New Role'}
                {editingRole?.is_system == 1 && (
                  <Badge variant="secondary" className="ml-2">System Role</Badge>
                )}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {viewMode && editingRole?.is_system == 1 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  Viewing system role. System roles cannot be edited or deleted.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Role Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="e.g., Sales Manager, Store Assistant"
                  disabled={viewMode || editingRole?.is_system == 1}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Describe what this role can do..."
                  disabled={viewMode || editingRole?.is_system == 1}
                />
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Permissions * ({formData.permission_ids.length} selected)
                </label>
                <div className="space-y-4 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4">
                  {Object.entries(permissions).map(([module, modulePermissions]) => {
                    const allSelected = modulePermissions.every(p => formData.permission_ids.includes(p.id))
                    const someSelected = modulePermissions.some(p => formData.permission_ids.includes(p.id))

                    return (
                      <div key={module} className="border-b border-gray-200 pb-4 last:border-b-0">
                        {/* Module Header */}
                        <div
                          className={`flex items-center gap-2 mb-2 p-2 rounded ${!viewMode ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                          onClick={() => !viewMode && toggleModulePermissions(modulePermissions)}
                        >
                          {allSelected ? (
                            <CheckSquare className="h-5 w-5 text-orange-500" />
                          ) : someSelected ? (
                            <div className="h-5 w-5 border-2 border-orange-500 rounded flex items-center justify-center">
                              <div className="h-2 w-2 bg-orange-500 rounded-sm" />
                            </div>
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                          <span className="font-semibold text-gray-800 capitalize">{module}</span>
                          <span className="text-xs text-gray-500">({modulePermissions.length} permissions)</span>
                        </div>

                        {/* Permission Checkboxes */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-7">
                          {modulePermissions.map(permission => (
                            <label
                              key={permission.id}
                              className={`flex items-start gap-2 p-2 rounded ${!viewMode ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.permission_ids.includes(permission.id)}
                                onChange={() => togglePermission(permission.id)}
                                className="mt-1"
                                disabled={viewMode}
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-700">
                                  {permission.display_name}
                                </div>
                                {permission.description && (
                                  <div className="text-xs text-gray-500">
                                    {permission.description}
                                  </div>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Active Status */}
              {!viewMode && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="mr-2"
                      disabled={editingRole?.is_system == 1}
                    />
                    <span className="text-sm text-gray-700">Active Role</span>
                  </label>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  {viewMode ? 'Close' : 'Cancel'}
                </button>
                {!viewMode && (
                  <button
                    type="submit"
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md"
                    disabled={formData.permission_ids.length === 0}
                  >
                    {editingRole ? 'Update' : 'Create'} Role
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRoles.map((role) => (
          <Card key={role.id} className="p-6 card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-orange-500" />
                <h3 className="text-lg font-semibold text-gray-800">{role.name}</h3>
              </div>
              <div className="flex gap-2">
                {role.is_system == 1 && (
                  <Badge variant="secondary">System</Badge>
                )}
                <Badge variant={role.is_active ? 'success' : 'secondary'}>
                  {role.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>

            {role.description && (
              <p className="text-gray-600 text-sm mb-4">{role.description}</p>
            )}

            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{role.permission_count || 0} permissions</span>
                <span>Created: {new Date(role.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex space-x-2">
              {role.is_system == 1 ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleView(role)}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <Shield className="h-3 w-3" />
                  View
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(role)}
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    <Edit2 className="h-3 w-3" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(role.id, role.name)}
                    className="flex-1 flex items-center justify-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </Button>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      {filteredRoles.length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">🛡️</div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">No roles found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm
              ? 'Try adjusting your search terms'
              : roles.length === 0
              ? 'Initialize default roles or create your first custom role'
              : 'Create your first custom role to get started'
            }
          </p>
          {!searchTerm && roles.length === 0 && (
            <div className="flex gap-2 justify-center">
              <Button onClick={handleInitializeRoles} variant="outline" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Initialize Default Roles
              </Button>
              <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Custom Role
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
